import {
  LineController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import Utils from 'general/utils/Utils';
import PropTypes from 'prop-types';
import { useMemo, useRef } from 'react';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  LineController,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

BarChartJS.propTypes = {
  horizontal: PropTypes.bool,
  title: PropTypes.string,
  showTitle: PropTypes.bool,
  labels: PropTypes.array,
  datasets: PropTypes.array,
  scales: PropTypes.array,
  dataType: PropTypes.string,
  highlightedIndex: PropTypes.number,
};

function BarChartJS({
  horizontal = false,
  title = '',
  showTitle = true,
  labels = [],
  datasets = [],
  scales = [],
  dataType = '',
  highlightedIndex = -1,
}) {
  const { hoverColor, stepSize, max, appendUnit, backgroundColor } = datasets[0] || {};

  const scalesOption = useMemo(() => {
    if (scales.length > 0) {
      const ret = {};
      const groupedScales = _.groupBy(scales, 'id');
      const keys = Object.keys(groupedScales);
      keys.forEach((key) => {
        const value = groupedScales[key][0];
        ret[key] = {
          position: value.position ?? 'left',
          display: true,
          beginAtZero: true,
          stacked: value.stacked ?? false,
          title: {
            display: value.title?.length > 0,
            text: value.title,
            color: '#181C32',
            font: {
              size: 12,
              family: 'IBM Plex Sans',
              weight: 600,
            },
          },
          grid: {
            drawOnChartArea: value.gridEnable ?? true,
          },
        };
      });
      return ret;
    }
    return {};
  }, [scales]);

  const refChart = useRef();

  const options = useMemo(() => {
    return {
      responsive: true,
      indexAxis: horizontal ? 'y' : 'x',
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: showTitle,
          text: title,
          color: '#181C32',
        },
        tooltip: {
          padding: 10,
          backgroundColor: hoverColor,
          titleColor: '#3F4254',
          titleFont: {
            size: 13,
            family: 'IBM Plex Sans',
            lineHeight: 1.2,
          },
          titleMarginBottom: 4,
          bodyColor: '#FFFFFF',
          bodySpacing: 4,
          bodyFont: {
            size: 12,
            family: 'IBM Plex Sans',
            lineHeight: 1.2,
            weight: '600',
          },
          borderWidth: 1,
          borderColor: hoverColor,
          borderRadius: 50,
          caretPadding: 6,
          cornerRadius: 16,
          width: 100,
          callbacks: {
            label: function (context) {
              return `${Utils.formatNumber(context.raw)} ${appendUnit ?? ''}`;
            },
            title: function () {
              return '';
            },
          },
          displayColors: false,
          yAlign: 'bottom',
        },
      },
      maintainAspectRatio: false,
      scales: {
        ...scalesOption,
        x: {
          ...scalesOption.x,
          grid: {
            display: false,
          },
          ticks: {
            display: true,
          },
        },
        y: {
          ...scalesOption.y,
          grid: {
            display: true,
          },
          ticks: {
            stepSize: stepSize,
            callback: function (value) {
              return Utils.formatNumber(value);
            }
          },
          max: max,
        },
      },
    };
  }, [title, showTitle, horizontal, scalesOption, labels, datasets]);

  const data = useMemo(() => {
    const barDataset = datasets.map((item) => ({
      type: 'bar',
      label: item.label ?? '',
      data: item.data,
      borderWidth: item.borderWidth ?? 2,
      borderColor: (context) => {
        const isActive = context.active;
        if (isActive) {
          return hoverColor;
        }
        return context.dataIndex === highlightedIndex ? hoverColor : backgroundColor;
      },
      backgroundColor: (context) => {
        const isActive = context.active;
        if (isActive) {
          return hoverColor;
        }
        return context.dataIndex === highlightedIndex ? hoverColor : backgroundColor;
      },
      yAxisID: item.yAxisID ?? 'y',
      borderRadius: item.borderRadius ?? 0,
    }));

    const lineDataset = {
      type: 'line',
      label: 'Line Dataset',
      data: datasets[0]?.data.map((value) => value),
      borderColor: hoverColor,
      borderWidth: 2,
      pointBackgroundColor: hoverColor,
      pointRadius: 2,
      fill: false,
      tension: 0.5,
    };

    return {
      labels: labels,
      datasets: dataType === 'revenue' ? [...barDataset, lineDataset] : barDataset,
    };
  }, [labels, datasets, hoverColor, backgroundColor]);

  return (
    <div className="h-100">
      <Bar options={options} data={data} ref={refChart} />
    </div>
  );
}

export default BarChartJS;
